import React, { useState } from 'react';
import { ArrowLeft, Check, CheckCircle, Copy, FilePdf } from 'phosphor-react';
import { CheckoutDTO } from '../../types/dtos';
import { BillingType } from '../../types/enums';
import TrocaIcone from '../ui/TrocaIcone';

const data = (iso: string) => new Date(`${iso}T00:00:00`).toLocaleDateString('pt-BR');

/**
 * Botão de copiar com confirmação visual.
 *
 * ponytail: `navigator.clipboard` é nativo e o "Copiado!" é um `useState` que
 * volta sozinho — não vale uma biblioteca de toast só por isto.
 */
function BotaoCopiar({ texto, rotulo }: { texto: string; rotulo: string }) {
  const [copiado, setCopiado] = useState(false);

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(texto);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2000);
    } catch {
      // Navegador sem permissão de área de transferência: o código continua
      // selecionável na tela, que é o que importa.
    }
  };

  return (
    <button
      type="button"
      onClick={copiar}
      className="flex items-center justify-center gap-2 w-full px-6 py-3.5 text-sm font-semibold rounded-button bg-blush-500 text-white hover:bg-blush-600 transition-colors"
    >
      {/* A troca seca passa despercebida, e é ela que confirma que copiou. */}
      <TrocaIcone
        ativo={copiado}
        quandoAtivo={<Check weight="bold" className="w-4 h-4" />}
        quandoInativo={<Copy className="w-4 h-4" />}
      />
      {copiado ? 'Copiado!' : rotulo}
    </button>
  );
}

/** Código longo em fonte monoespaçada, quebrando linha em vez de estourar a largura. */
function Codigo({ valor }: { valor: string }) {
  return (
    <p className="text-[11px] font-mono break-all text-gray-600 dark:text-gray-300 bg-nude-50 dark:bg-gray-800 rounded-xl p-3 select-all">
      {valor}
    </p>
  );
}

interface PaymentInstructionsProps {
  checkout: CheckoutDTO;
  /** Volta ao formulário — para trocar de plano ou de forma de pagamento. */
  onVoltar: () => void;
}

/**
 * O que o usuário faz depois de confirmar, sem sair do app: escaneia o QR do Pix,
 * copia a linha do boleto, ou apenas vê que o cartão passou.
 *
 * <p>A confirmação de Pix e boleto ainda vem do webhook do Asaas — esta tela só
 * mostra como pagar. O status acima dela é quem diz se o acesso liberou.</p>
 */
export default function PaymentInstructions({ checkout, onVoltar }: PaymentInstructionsProps) {
  const voltar = (
    <button
      type="button"
      onClick={onVoltar}
      className="flex items-center justify-center gap-2 text-xs font-semibold text-nude-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors py-2"
    >
      <ArrowLeft className="w-3.5 h-3.5" />
      Escolher outra forma de pagamento
    </button>
  );

  if (checkout.billingType === BillingType.CREDIT_CARD) {
    return (
      <div className="flex flex-col items-center gap-3 p-6 rounded-2xl bg-sage-100 dark:bg-sage-900/30 text-center">
        <CheckCircle weight="fill" className="w-10 h-10 text-sage-700 dark:text-sage-300" />
        <div>
          <p className="text-sm font-semibold text-sage-700 dark:text-sage-200">Pagamento aprovado</p>
          <p className="text-xs text-sage-700/80 dark:text-sage-300/80 mt-1">
            A renovação é automática a cada ciclo. Você pode cancelar quando quiser.
          </p>
        </div>
      </div>
    );
  }

  if (checkout.billingType === BillingType.PIX) {
    return (
      <div className="flex flex-col gap-4 p-5 rounded-2xl bg-white dark:bg-gray-900 border-2 border-nude-200 dark:border-gray-700">
        <div>
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Pague com Pix</p>
          <p className="text-xs text-nude-500 dark:text-gray-400 mt-0.5">
            Escaneie o código no app do seu banco. O acesso libera assim que o pagamento cair.
          </p>
        </div>

        {checkout.pixQrCode && (
          <img
            src={`data:image/png;base64,${checkout.pixQrCode}`}
            alt="QR Code do Pix"
            className="w-52 h-52 mx-auto rounded-xl bg-white p-2"
          />
        )}

        {checkout.pixPayload && (
          <>
            <Codigo valor={checkout.pixPayload} />
            <BotaoCopiar texto={checkout.pixPayload} rotulo="Copiar código Pix" />
          </>
        )}

        {!checkout.pixQrCode && !checkout.pixPayload && (
          <p className="text-xs text-red-500">
            Não conseguimos gerar o QR Code agora. Use o botão “Pagar cobrança em aberto” acima.
          </p>
        )}

        {voltar}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-5 rounded-2xl bg-white dark:bg-gray-900 border-2 border-nude-200 dark:border-gray-700">
      <div>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Pague com boleto</p>
        <p className="text-xs text-nude-500 dark:text-gray-400 mt-0.5">
          {checkout.dueDate ? `Vence em ${data(checkout.dueDate)}. ` : ''}
          A compensação leva até 3 dias úteis.
        </p>
      </div>

      {checkout.boletoLine && (
        <>
          <Codigo valor={checkout.boletoLine} />
          <BotaoCopiar texto={checkout.boletoLine} rotulo="Copiar linha digitável" />
        </>
      )}

      {checkout.boletoUrl && (
        <a
          href={checkout.boletoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center justify-center gap-2 px-6 py-3.5 text-sm font-semibold rounded-button border-2 border-blush-200 text-gray-700 dark:text-gray-200 hover:bg-blush-50 dark:hover:bg-gray-800 transition-colors"
        >
          <FilePdf className="w-4 h-4" />
          Abrir o boleto em PDF
        </a>
      )}

      {voltar}
    </div>
  );
}
